import type {
  AdminAssessmentsResponse,
  AdminDashboardSummary,
  AdminDemoRequestPeriod,
  AdminDemoRequestsResponse,
  AdminDocumentsResponse,
  AdminFeatureFlag,
  AdminIncidentsResponse,
  AdminLogoutResult,
  AdminMembershipRemovalResult,
  AdminPasswordResetResult,
  AdminPipelineRun,
  AdminTenantListItem,
  AdminTenantMember,
  AdminUserListItem,
  AdminUsersListResponse,
  GetAssessmentResultsResponse,
  PlatformAdminGrantResult,
} from '@libs/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { apiClient } from '@api';
import { queryKeys } from '@constants';
import { FeatureFlagKey } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { getLocalizedErrorMessage } from '../../assessment/utils/error-utils';
import type {
  ActiveTemplateCategory,
  ActiveTemplateOutline,
} from '../../tenant-profile-hub/api/types';

// ============================================================================
// REQUEST BODIES (admin-only, not part of the shared @libs/types surface)
// ============================================================================

/** Body for provisioning a new tenant (Keycloak Organization). */
export interface CreateTenantDto {
  /** Organization slug — must match `^[a-z0-9-]+$`. */
  name: string;
  displayName: string;
  contactEmail: string;
  sector?: string;
  /** Organization location (city / country). */
  location?: string;
}

/** Body for creating a user inside a tenant. */
export interface CreateTenantUserDto {
  email: string;
  firstName: string;
  lastName: string;
}

/** Body for editing an existing tenant (partial). Slug (`name`) is immutable. */
export interface UpdateTenantDto {
  displayName?: string;
  contactEmail?: string;
  sector?: string;
  dpo?: string;
  commercialRegistration?: string;
  location?: string;
}

/** Body for editing an existing user (partial). */
export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  enabled?: boolean;
}

/** Body for setting a user's password. */
export interface SetUserPasswordDto {
  password: string;
  /** When true, the user must change it at next login. */
  temporary: boolean;
}

/** Shared pagination params for the paginated admin endpoints. */
export interface AdminPageParams {
  page?: number;
  limit?: number;
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

const buildPageParams = (params?: AdminPageParams & { search?: string }) => {
  const query = new URLSearchParams();
  if (params?.search) query.append('search', params.search);
  if (params?.page) query.append('page', String(params.page));
  if (params?.limit) query.append('limit', String(params.limit));
  return query;
};

async function getDashboardSummary(signal?: AbortSignal): Promise<AdminDashboardSummary> {
  const { data } = await apiClient.get<AdminDashboardSummary>('api/admin/dashboard/summary', {
    signal,
  });
  return data;
}

async function getInFlight(signal?: AbortSignal): Promise<AdminPipelineRun[]> {
  const { data } = await apiClient.get<AdminPipelineRun[]>('api/admin/dashboard/in-flight', {
    signal,
  });
  return data;
}

async function getIncidents(
  params?: AdminPageParams,
  signal?: AbortSignal,
): Promise<AdminIncidentsResponse> {
  const { data } = await apiClient.get<AdminIncidentsResponse>('api/admin/dashboard/incidents', {
    params: buildPageParams(params),
    signal,
  });
  return data;
}

async function getTenants(search?: string, signal?: AbortSignal): Promise<AdminTenantListItem[]> {
  const { data } = await apiClient.get<AdminTenantListItem[]>('api/admin/tenants', {
    params: buildPageParams({ search }),
    signal,
  });
  return data;
}

async function getTenant(id: string, signal?: AbortSignal): Promise<AdminTenantListItem> {
  const { data } = await apiClient.get<AdminTenantListItem>(`api/admin/tenants/${id}`, { signal });
  return data;
}

async function getTenantMembers(id: string, signal?: AbortSignal): Promise<AdminTenantMember[]> {
  const { data } = await apiClient.get<AdminTenantMember[]>(`api/admin/tenants/${id}/members`, {
    signal,
  });
  return data;
}

async function getTenantAssessments(
  id: string,
  params?: AdminPageParams,
  signal?: AbortSignal,
): Promise<AdminAssessmentsResponse> {
  const { data } = await apiClient.get<AdminAssessmentsResponse>(
    `api/admin/tenants/${id}/assessments`,
    { params: buildPageParams(params), signal },
  );
  return data;
}

async function getTenantDocuments(
  id: string,
  params?: AdminPageParams,
  signal?: AbortSignal,
): Promise<AdminDocumentsResponse> {
  const { data } = await apiClient.get<AdminDocumentsResponse>(
    `api/admin/tenants/${id}/documents`,
    { params: buildPageParams(params), signal },
  );
  return data;
}

async function getAssessmentDocuments(
  tenantId: string,
  assessmentId: string,
  params?: AdminPageParams,
  signal?: AbortSignal,
): Promise<AdminDocumentsResponse> {
  const { data } = await apiClient.get<AdminDocumentsResponse>(
    `api/admin/tenants/${tenantId}/assessments/${assessmentId}/documents`,
    { params: buildPageParams(params), signal },
  );
  return data;
}

/** Save a Blob to disk via a transient object-URL anchor. */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Fetch a presigned URL for one admin document, then download its bytes. */
async function downloadAssessmentDocument(
  tenantId: string,
  documentId: string,
  fileName: string,
): Promise<void> {
  const { data } = await apiClient.get<{ s3DownloadUrl: string }>(
    `api/admin/tenants/${tenantId}/documents/${documentId}/download-url`,
  );
  const blob = await fetch(data.s3DownloadUrl).then((r) => r.blob());
  triggerBlobDownload(blob, fileName || 'document');
}

/** Stream all of an assessment's documents as a ZIP and save it. */
async function downloadAssessmentDocumentsZip(
  tenantId: string,
  assessmentId: string,
): Promise<void> {
  const response = await apiClient.get(
    `api/admin/tenants/${tenantId}/assessments/${assessmentId}/documents/download-zip`,
    { responseType: 'blob' },
  );

  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  let filename = `documents_${assessmentId}.zip`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="([^"]+)"/);
    if (match?.[1]) filename = match[1];
  }

  triggerBlobDownload(
    new Blob([response.data as BlobPart], { type: 'application/zip' }),
    filename,
  );
}

async function getTenantProfileCategories(
  id: string,
  signal?: AbortSignal,
): Promise<ActiveTemplateOutline> {
  const { data } = await apiClient.get<ActiveTemplateOutline>(
    `api/admin/tenants/${id}/profile/categories`,
    { signal },
  );
  return data;
}

async function getTenantProfileCategory(
  id: string,
  categoryId: string,
  signal?: AbortSignal,
): Promise<ActiveTemplateCategory> {
  const { data } = await apiClient.get<ActiveTemplateCategory>(
    `api/admin/tenants/${id}/profile/categories/${categoryId}`,
    { signal },
  );
  return data;
}

async function getAdminAssessmentResults(
  tenantId: string,
  assessmentId: string,
  signal?: AbortSignal,
): Promise<GetAssessmentResultsResponse> {
  const { data } = await apiClient.get<GetAssessmentResultsResponse>(
    `api/admin/tenants/${tenantId}/assessments/${assessmentId}/results`,
    { signal },
  );
  return data;
}

/** Fetch a presigned URL for an assessment's report PDF and open it. */
async function downloadAdminReport(tenantId: string, assessmentId: string): Promise<void> {
  const { data } = await apiClient.get<{ s3DownloadUrl: string }>(
    `api/admin/tenants/${tenantId}/assessments/${assessmentId}/report/download-url`,
  );
  window.open(data.s3DownloadUrl, '_blank');
}

async function provisionTenant(body: CreateTenantDto): Promise<AdminTenantListItem> {
  const { data } = await apiClient.post<AdminTenantListItem>('api/admin/tenants', body);
  return data;
}

async function createTenantUser(
  tenantId: string,
  body: CreateTenantUserDto,
): Promise<AdminTenantMember> {
  const { data } = await apiClient.post<AdminTenantMember>(
    `api/admin/tenants/${tenantId}/users`,
    body,
  );
  return data;
}

async function revokeTenantUser(
  tenantId: string,
  userId: string,
): Promise<AdminMembershipRemovalResult> {
  const { data } = await apiClient.delete<AdminMembershipRemovalResult>(
    `api/admin/tenants/${tenantId}/users/${userId}`,
  );
  return data;
}

async function updateTenant(id: string, body: UpdateTenantDto): Promise<AdminTenantListItem> {
  const { data } = await apiClient.patch<AdminTenantListItem>(`api/admin/tenants/${id}`, body);
  return data;
}

async function updateUser(userId: string, body: UpdateUserDto): Promise<AdminUserListItem> {
  const { data } = await apiClient.patch<AdminUserListItem>(`api/admin/users/${userId}`, body);
  return data;
}

async function setUserPassword(
  userId: string,
  body: SetUserPasswordDto,
): Promise<AdminPasswordResetResult> {
  const { data } = await apiClient.put<AdminPasswordResetResult>(
    `api/admin/users/${userId}/reset-password`,
    body,
  );
  return data;
}

async function getUsers(
  params?: AdminPageParams & { search?: string },
  signal?: AbortSignal,
): Promise<AdminUsersListResponse> {
  const { data } = await apiClient.get<AdminUsersListResponse>('api/admin/users', {
    params: buildPageParams(params),
    signal,
  });
  return data;
}

/** Params for the read-only demo-requesters list (period window + pagination). */
export interface AdminDemoRequestsParams extends AdminPageParams {
  period?: AdminDemoRequestPeriod;
}

async function getDemoRequests(
  params?: AdminDemoRequestsParams,
  signal?: AbortSignal,
): Promise<AdminDemoRequestsResponse> {
  const query = buildPageParams({ page: params?.page, limit: params?.limit });
  if (params?.period) query.append('period', params.period);
  const { data } = await apiClient.get<AdminDemoRequestsResponse>('api/admin/demo-requests', {
    params: query,
    signal,
  });
  return data;
}

async function getFeatureFlags(signal?: AbortSignal): Promise<AdminFeatureFlag[]> {
  const { data } = await apiClient.get<AdminFeatureFlag[]>('api/admin/features', { signal });
  return data;
}

/** Sets a feature flag's environment-wide default. */
async function updateFeatureFlag(
  key: FeatureFlagKey,
  enabled: boolean,
): Promise<{ key: FeatureFlagKey; enabled: boolean }> {
  const { data } = await apiClient.put(`api/admin/features/${key}`, { enabled });
  return data;
}

/** Sets (`enabled: true/false`) or clears (`enabled: null`) one tenant's override. */
async function updateTenantFeatureFlag(
  tenantId: string,
  key: FeatureFlagKey,
  enabled: boolean | null,
): Promise<{ key: FeatureFlagKey; tenantId: string; enabled: boolean | null }> {
  const { data } = await apiClient.put(`api/admin/tenants/${tenantId}/features/${key}`, { enabled });
  return data;
}

async function grantPlatformAdmin(userId: string): Promise<PlatformAdminGrantResult> {
  const { data } = await apiClient.post<PlatformAdminGrantResult>(
    `api/admin/users/${userId}/platform-admin`,
  );
  return data;
}

async function revokePlatformAdmin(userId: string): Promise<PlatformAdminGrantResult> {
  const { data } = await apiClient.delete<PlatformAdminGrantResult>(
    `api/admin/users/${userId}/platform-admin`,
  );
  return data;
}

async function forceLogoutUser(userId: string): Promise<AdminLogoutResult> {
  const { data } = await apiClient.post<AdminLogoutResult>(`api/admin/users/${userId}/logout`);
  return data;
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

export const useAdminDashboardSummary = () =>
  useQuery({
    queryKey: queryKeys.admin.dashboardSummary,
    queryFn: ({ signal }) => getDashboardSummary(signal),
  });

export const useAdminInFlight = () =>
  useQuery({
    queryKey: queryKeys.admin.inFlight,
    queryFn: ({ signal }) => getInFlight(signal),
  });

export const useAdminIncidents = (params?: AdminPageParams) =>
  useQuery({
    queryKey: queryKeys.admin.incidents(params),
    queryFn: ({ signal }) => getIncidents(params, signal),
  });

export const useAdminTenants = (search?: string) =>
  useQuery({
    queryKey: queryKeys.admin.tenants({ search }),
    queryFn: ({ signal }) => getTenants(search, signal),
  });

export const useAdminTenant = (id: string) =>
  useQuery({
    queryKey: queryKeys.admin.tenant(id),
    queryFn: ({ signal }) => getTenant(id, signal),
    enabled: !!id,
  });

export const useAdminTenantMembers = (id: string) =>
  useQuery({
    queryKey: queryKeys.admin.tenantMembers(id),
    queryFn: ({ signal }) => getTenantMembers(id, signal),
    enabled: !!id,
  });

export const useAdminTenantAssessments = (id: string, params?: AdminPageParams) =>
  useQuery({
    queryKey: queryKeys.admin.tenantAssessments(id, params),
    queryFn: ({ signal }) => getTenantAssessments(id, params, signal),
    enabled: !!id,
  });

export const useAdminTenantDocuments = (id: string, params?: AdminPageParams) =>
  useQuery({
    queryKey: queryKeys.admin.tenantDocuments(id, params),
    queryFn: ({ signal }) => getTenantDocuments(id, params, signal),
    enabled: !!id,
  });

export const useAdminAssessmentDocuments = (
  tenantId: string,
  assessmentId: string | null,
  params?: AdminPageParams,
) =>
  useQuery({
    queryKey: queryKeys.admin.assessmentDocuments(tenantId, assessmentId ?? '', params),
    queryFn: ({ signal }) => getAssessmentDocuments(tenantId, assessmentId as string, params, signal),
    enabled: !!tenantId && !!assessmentId,
  });

export const useAdminTenantProfileCategories = (id: string) =>
  useQuery({
    queryKey: queryKeys.admin.profileCategories(id),
    queryFn: ({ signal }) => getTenantProfileCategories(id, signal),
    enabled: !!id,
  });

export const useAdminTenantProfileCategory = (id: string, categoryId: string | null) =>
  useQuery({
    queryKey: queryKeys.admin.profileCategory(id, categoryId ?? ''),
    queryFn: ({ signal }) => getTenantProfileCategory(id, categoryId as string, signal),
    enabled: !!id && !!categoryId,
  });

export const useAdminAssessmentResults = (
  tenantId: string,
  assessmentId: string | null,
) =>
  useQuery({
    queryKey: queryKeys.admin.assessmentResults(tenantId, assessmentId ?? ''),
    queryFn: ({ signal }) => getAdminAssessmentResults(tenantId, assessmentId as string, signal),
    enabled: !!tenantId && !!assessmentId,
  });

export const useDownloadAdminReport = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, { tenantId: string; assessmentId: string }>({
    mutationFn: ({ tenantId, assessmentId }) => downloadAdminReport(tenantId, assessmentId),
    onError: (error) => {
      enqueueSnackbar(
        getLocalizedErrorMessage(error, t, t('app.admin.detail.assessTab.downloadReport.error')),
        { variant: 'error' },
      );
    },
  });
};

export const useDownloadAdminDocument = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, { tenantId: string; documentId: string; fileName: string }>({
    mutationFn: ({ tenantId, documentId, fileName }) =>
      downloadAssessmentDocument(tenantId, documentId, fileName),
    onError: (error) => {
      enqueueSnackbar(
        getLocalizedErrorMessage(error, t, t('app.admin.detail.docsTab.download.error')),
        { variant: 'error' },
      );
    },
  });
};

export const useDownloadAdminAssessmentDocumentsZip = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, { tenantId: string; assessmentId: string }>({
    mutationFn: ({ tenantId, assessmentId }) =>
      downloadAssessmentDocumentsZip(tenantId, assessmentId),
    onError: (error) => {
      enqueueSnackbar(
        getLocalizedErrorMessage(error, t, t('app.admin.detail.docsTab.downloadAll.error')),
        { variant: 'error' },
      );
    },
  });
};

export const useAdminUsers = (params?: AdminPageParams & { search?: string }) =>
  useQuery({
    queryKey: queryKeys.admin.users(params),
    queryFn: ({ signal }) => getUsers(params, signal),
  });

export const useAdminDemoRequests = (params?: AdminDemoRequestsParams) =>
  useQuery({
    queryKey: queryKeys.admin.demoRequests(params),
    queryFn: ({ signal }) => getDemoRequests(params, signal),
  });

export const useAdminFeatureFlags = () =>
  useQuery({
    queryKey: queryKeys.admin.features,
    queryFn: ({ signal }) => getFeatureFlags(signal),
  });

// ============================================================================
// MUTATION HOOKS
// ============================================================================

export const useProvisionTenant = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminTenantListItem, Error, CreateTenantDto>({
    mutationFn: provisionTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboardSummary });
      enqueueSnackbar(t('app.admin.provision.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.provision.error')), {
        variant: 'error',
      });
    },
  });
};

export const useCreateTenantUser = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminTenantMember, Error, CreateTenantUserDto>({
    mutationFn: (body) => createTenantUser(tenantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tenantMembers(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tenant(tenantId) });
      enqueueSnackbar(t('app.admin.addUser.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.addUser.error')), {
        variant: 'error',
      });
    },
  });
};

export const useRevokeTenantUser = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminMembershipRemovalResult, Error, string>({
    mutationFn: (userId) => revokeTenantUser(tenantId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tenantMembers(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tenant(tenantId) });
      enqueueSnackbar(t('app.admin.detail.usersTab.revoke.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(
        getLocalizedErrorMessage(error, t, t('app.admin.detail.usersTab.revoke.error')),
        { variant: 'error' },
      );
    },
  });
};

export const useUpdateTenant = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminTenantListItem, Error, UpdateTenantDto>({
    mutationFn: (body) => updateTenant(tenantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.tenant(tenantId) });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      enqueueSnackbar(t('app.admin.editTenant.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.editTenant.error')), {
        variant: 'error',
      });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminUserListItem, Error, { userId: string; body: UpdateUserDto }>({
    mutationFn: ({ userId, body }) => updateUser(userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant'] });
      enqueueSnackbar(t('app.admin.editUser.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.editUser.error')), {
        variant: 'error',
      });
    },
  });
};

export const useSetUserPassword = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminPasswordResetResult, Error, { userId: string; body: SetUserPasswordDto }>({
    mutationFn: ({ userId, body }) => setUserPassword(userId, body),
    onSuccess: () => {
      enqueueSnackbar(t('app.admin.setPassword.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.setPassword.error')), {
        variant: 'error',
      });
    },
  });
};

export const useGrantPlatformAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<PlatformAdminGrantResult, Error, string>({
    mutationFn: grantPlatformAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      enqueueSnackbar(t('app.admin.users.grant.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.users.grant.error')), {
        variant: 'error',
      });
    },
  });
};

export const useRevokePlatformAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<PlatformAdminGrantResult, Error, string>({
    mutationFn: revokePlatformAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      enqueueSnackbar(t('app.admin.users.revoke.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.users.revoke.error')), {
        variant: 'error',
      });
    },
  });
};

export const useForceLogoutUser = () => {
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<AdminLogoutResult, Error, string>({
    mutationFn: forceLogoutUser,
    onSuccess: () => {
      enqueueSnackbar(t('app.admin.users.logout.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.users.logout.error')), {
        variant: 'error',
      });
    },
  });
};

export const useUpdateFeatureFlag = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    { key: FeatureFlagKey; enabled: boolean },
    Error,
    { key: FeatureFlagKey; enabled: boolean }
  >({
    mutationFn: ({ key, enabled }) => updateFeatureFlag(key, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.features });
      enqueueSnackbar(t('app.admin.features.update.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.features.update.error')), {
        variant: 'error',
      });
    },
  });
};

export const useUpdateTenantFeatureFlag = () => {
  const queryClient = useQueryClient();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    { key: FeatureFlagKey; tenantId: string; enabled: boolean | null },
    Error,
    { tenantId: string; key: FeatureFlagKey; enabled: boolean | null }
  >({
    mutationFn: ({ tenantId, key, enabled }) => updateTenantFeatureFlag(tenantId, key, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.features });
      enqueueSnackbar(t('app.admin.features.update.success'), { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(getLocalizedErrorMessage(error, t, t('app.admin.features.update.error')), {
        variant: 'error',
      });
    },
  });
};
